/*
 * This component is responsible for rendering a modal.
 * It takes props to populate the body as well as the footer.
 *
 * @author Cargomatic Engineering
 * @usage <Modal />
 *
 */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from 'components/v2/buttons/Button';
import classNames from 'classnames';
export const Modal = ({ title, isOpen, children, onClose, initialFocus, isStatic, panelClassName, dataTestId }) => {
    return ((React.createElement(Transition.Root, { "data-testid": dataTestId, show: isOpen, as: Fragment },
        React.createElement(Dialog, { as: "div", className: "relative z-50", onClose: onClose, initialFocus: initialFocus, static: isStatic },
            React.createElement(Transition.Child, { "data-testid": "div-a03ecd7b", as: Fragment, enter: "ease-out duration-300", enterFrom: "opacity-0", enterTo: "opacity-100", leave: "ease-in duration-200", leaveFrom: "opacity-100", leaveTo: "opacity-0" },
                React.createElement("div", { "data-testid": "div-6b19e4bf", className: "fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" })),
            React.createElement("div", { "data-testid": "div-fe8afeab", className: "fixed inset-0 z-10 w-screen overflow-y-auto" },
                React.createElement("div", { "data-testid": "div-7f826064", className: "flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0" },
                    React.createElement(Transition.Child, { "data-testid": "div-6fcb3994", as: Fragment, enter: "ease-out duration-300", enterFrom: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95", enterTo: "opacity-100 translate-y-0 sm:scale-100", leave: "ease-in duration-200", leaveFrom: "opacity-100 translate-y-0 sm:scale-100", leaveTo: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" },
                        React.createElement(Dialog.Panel, { "data-testid": "div-9091d4b6", className: `relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg divide-y divide-gray-200 ${panelClassName}` },
                            React.createElement("div", { "data-testid": "div-ac97322b", className: "px-4 py-4" },
                                React.createElement(Dialog.Title, { "data-testid": "div-59e4d156", as: "h3", className: "text-base font-semibold leading-6 text-gray-900" }, title),
                                React.createElement("div", { "data-testid": "div-a2e5f9da", className: "absolute right-0 top-0 hidden pr-4 py-4 sm:block" },
                                    React.createElement("button", { "data-testid": "button-ba4de080", type: "button", className: "rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2", onClick: onClose },
                                        React.createElement("span", { "data-testid": "span-5b069c4b", className: "sr-only" }, "Close"),
                                        React.createElement(XMarkIcon, { className: "h-6 w-6", "aria-hidden": "true" })))),
                            children))))))));
};
Modal.propTypes = {
    isOpen: PropTypes.bool, // isOpen the modal
    onClose: PropTypes.func.isRequired, // onClose Callback
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired, // Modal Title
    children: PropTypes.any,
    isStatic: PropTypes.bool,
    panelClassName: PropTypes.string,
};
Modal.defaultProps = {
    isOpen: false,
    isStatic: false,
};
export function ModalActions({ primaryAction, secondaryActions, className }) {
    return ((React.createElement("div", { "data-testid": "div-9403c483", className: classNames('bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 sm:gap-x-4 rounded-b-lg', className) },
        React.createElement(Button, { dataTestId: "Button-9322d326", onClick: primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.onClick, color: (primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.color) || 'green', disabled: primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.disabled }, primaryAction.label || 'Save'),
        (secondaryActions || []).map((secondaryAction, i) => (React.createElement(Button, { dataTestId: `Button-1bb30dce-${i}`, onClick: secondaryAction === null || secondaryAction === void 0 ? void 0 : secondaryAction.onClick, disabled: secondaryAction === null || secondaryAction === void 0 ? void 0 : secondaryAction.disabled, key: i }, secondaryAction === null || secondaryAction === void 0 ? void 0 : secondaryAction.label))))));
}
ModalActions.propTypes = {
    primaryAction: PropTypes.shape({
        label: PropTypes.string,
        color: PropTypes.string,
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
    }).isRequired,
    secondaryActions: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        color: PropTypes.string,
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
    })),
};
Modal.Actions = ModalActions;
export function ModalBody({ children }) {
    return React.createElement("div", { "data-testid": "div-e797f5d3", className: "px-4 py-5 sm:py-6" }, children);
}
Modal.Body = ModalBody;
export function ConfirmActionModal({ title, isOpen, onClose, children, onConfirm, confirmActionText, isStatic, confirmActionColor, actionDisabled, dataTestId }) {
    const primaryAction = {
        label: confirmActionText,
        onClick: onConfirm,
        color: confirmActionColor,
        disabled: actionDisabled,
    };
    const secondaryActions = [
        {
            label: 'Cancel',
            onClick: onClose,
        },
    ];
    return (React.createElement(Modal, { dataTestId: dataTestId, title: title, isOpen: isOpen, onClose: onClose, isStatic: isStatic },
        React.createElement(ModalBody, null, children),
        React.createElement(ModalActions, { className: "mt-4", primaryAction: primaryAction, secondaryActions: secondaryActions })));
}
